import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import { fetchCategories, updateCategory, clearError, clearSuccess } from '../../../redux/Slices/AdminCategoriesSlice';
import { compressFiles } from '../../../shared/utils/performanceUtils';

const AdminEditCategory = () => {
    const { setTitle } = useTitle();
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { categories, updating, error, success } = useSelector((state) => state.adminCategories);
    
    const [formData, setFormData] = useState({
        name_ar: '',
        name_en: '',
        logo: null,
        web_img: null
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [webImgPreview, setWebImgPreview] = useState(null);
    const [imageProcessing, setImageProcessing] = useState(false);

    useEffect(() => {
        setTitle('تعديل الفئة');
        if (categories.length === 0) {
            dispatch(fetchCategories());
        }
    }, [setTitle, dispatch, categories.length]);

    useEffect(() => {
        const category = categories.find(cat => cat.id === parseInt(categoryId));
        if (category) {
            setFormData({
                name_ar: category.name?.ar || '',
                name_en: category.name?.en || '',
                logo: null,
                web_img: null
            });
            setLogoPreview(category.logo);
            setWebImgPreview(category.web_img || category.web_image || null);
        }
    }, [categories, categoryId]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            toast.success(success);
            dispatch(clearSuccess());
            setTimeout(() => {
                navigate('/admin-categories');
            }, 1500);
        }
    }, [success, dispatch, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];

        if (!file) {
            setFormData(prev => ({
                ...prev,
                logo: null
            }));
            setLogoPreview(categories.find(cat => cat.id === parseInt(categoryId))?.logo || null);
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast.error('يرجى اختيار صورة صالحة');
            e.target.value = '';
            return;
        }

        setImageProcessing(true);

        try {
            // إذا كان الملف GIF، نرفعه مباشرة بدون ضغط للحفاظ على الحركة
            if (file.type === 'image/gif') {
                setFormData(prev => ({
                    ...prev,
                    logo: file
                }));
                setLogoPreview(URL.createObjectURL(file));
            } else {
                // للصور الأخرى، نستخدم الضغط
                const [compressedResult] = await compressFiles([file], {
                    maxSize: 800000,
                    quality: 0.85
                });

                const finalFile = compressedResult instanceof File
                    ? compressedResult
                    : new File([compressedResult], file.name, {
                        type: file.type || 'image/jpeg',
                        lastModified: Date.now()
                    });

                setFormData(prev => ({
                    ...prev,
                    logo: finalFile
                }));
                setLogoPreview(URL.createObjectURL(finalFile));
            }
        } catch (error) {
            toast.error('فشل في معالجة الصورة، يرجى المحاولة مرة أخرى');
        } finally {
            setImageProcessing(false);
        }
    };

    const handleWebImgChange = async (e) => {
        const file = e.target.files[0];

        if (!file) {
            setFormData(prev => ({
                ...prev,
                web_img: null
            }));
            setWebImgPreview(categories.find(cat => cat.id === parseInt(categoryId))?.web_img || null);
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast.error('يرجى اختيار صورة صالحة');
            e.target.value = '';
            return;
        }

        setImageProcessing(true);

        try {
            // إذا كان الملف GIF، نرفعه مباشرة بدون ضغط للحفاظ على الحركة
            if (file.type === 'image/gif') {
                setFormData(prev => ({
                    ...prev,
                    web_img: file
                }));
                setWebImgPreview(URL.createObjectURL(file));
            } else {
                // للصور الأخرى، نستخدم الضغط
                const [compressedResult] = await compressFiles([file], {
                    maxSize: 800000,
                    quality: 0.85
                });

                const finalFile = compressedResult instanceof File
                    ? compressedResult
                    : new File([compressedResult], file.name, {
                        type: file.type || 'image/jpeg',
                        lastModified: Date.now()
                    });

                setFormData(prev => ({
                    ...prev,
                    web_img: finalFile
                }));
                setWebImgPreview(URL.createObjectURL(finalFile));
            }
        } catch (error) {
            toast.error('فشل في معالجة الصورة، يرجى المحاولة مرة أخرى');
        } finally {
            setImageProcessing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = new FormData();
        data.append('name_ar', formData.name_ar);
        data.append('name_en', formData.name_en);
        if (formData.logo) {
            data.append('logo', formData.logo);
        }
        if (formData.web_img) {
            data.append('web_img', formData.web_img);
        }

        try {
            await dispatch(updateCategory({ 
                categoryId, 
                formData: data 
            })).unwrap();
        } catch (err) {
            const message = typeof err === 'string' ? err : err?.message || 'فشل في تحديث الفئة';
            toast.error(message);
        }
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                <ToastContainer limit={1} />
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/admin-categories')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة للفئات
                    </motion.button>
                </div>

                {/* Edit Form */}
                <div className="card shadow-lg border-0">
                    <div className="card-header">
                        <h5 className="mb-0">تعديل الفئة</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">الاسم بالعربية</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">الاسم بالإنجليزية</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">اللوجو</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                        disabled={imageProcessing}
                                    />
                                    {logoPreview && (
                                        <div className="mt-3">
                                            <img
                                                src={logoPreview}
                                                alt="Preview"
                                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                className="rounded"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">صورة الويب</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleWebImgChange}
                                        disabled={imageProcessing}
                                    />
                                    {(webImgPreview || formData.web_img) && (
                                        <div className="mt-3">
                                            <img
                                                src={webImgPreview}
                                                alt="Web Preview"
                                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                className="rounded"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex gap-2 justify-content-center">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => navigate('/admin-categories')}
                                    disabled={updating}>
                                    إلغاء
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn text-white"
                                    style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                    disabled={updating || imageProcessing}>
                                    {updating ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminEditCategory;

